/**
 * Client wrapper for the IK worker.
 *
 * Provides an async interface for dispatching IK solves and corrective weight
 * computations to the web worker. Uses sequence numbers to discard stale
 * results when a newer solve overtakes an in-flight one.
 *
 * Integration points:
 *   - posePipeline.ts calls solveViaWorker() for IK translation/whole-body
 *   - MannequinRenderer.tsx calls requestCorrectiveWeights() per-frame
 *   - storeCommandOps.ts calls createWorkerSession()/destroyWorkerSession()
 */

import type {
  IKWorkerInit,
  IKWorkerMessage,
  IKWorkerResult,
  IKWorkerSolveChain,
  IKWorkerSolveFK,
  IKWorkerSolveWholeBody,
} from "./ik.worker.ts";
import type {
  IKChainStateCore,
  JointConstraint,
  SkeletonStateCore,
  WholeBodyTargetCore,
} from "./ikTypes.ts";
import type { Quat, Vec3 } from "./workerMath.ts";

// ---------------------------------------------------------------------------
// Worker singleton
// ---------------------------------------------------------------------------

let _worker: Worker | null = null;
let _initialized = false;
let _seq = 0;

function getWorker(): Worker {
  if (!_worker) {
    _worker = new Worker(new URL("./ik.worker.ts", import.meta.url), {
      type: "module",
    });
  }
  return _worker;
}

// ---------------------------------------------------------------------------
// Promise-based message dispatch
// ---------------------------------------------------------------------------

function postAndWait<T extends IKWorkerMessage>(
  msg: T,
): Promise<IKWorkerResult> {
  return new Promise((resolve) => {
    const worker = getWorker();
    ++_seq;

    const onMessage = (e: MessageEvent) => {
      // Accept any response — since we only care about latest result,
      // and we track seq externally for staleness
      worker.removeEventListener("message", onMessage);
      resolve(e.data as IKWorkerResult);
    };

    worker.addEventListener("message", onMessage);
    worker.postMessage(msg);
  });
}

/** Fire-and-forget send (no response needed). */
function postFire(msg: IKWorkerMessage): void {
  getWorker().postMessage(msg);
}

// ---------------------------------------------------------------------------
// Latest-result pattern for per-frame IK solving
// ---------------------------------------------------------------------------

/** Latest pending solve result. Callers poll this instead of awaiting. */
let _latestResult: IKWorkerResult | null = null;
let _solveInFlight = false;
let _solveSeq = 0;

/**
 * Dispatch a solve and stash the result when it arrives.
 * If a solve is already in-flight, the new request replaces it
 * (the old result will be discarded via sequence number).
 */
function dispatchSolve(msg: IKWorkerSolveChain | IKWorkerSolveWholeBody): void {
  const seq = ++_solveSeq;
  _solveInFlight = true;

  const worker = getWorker();

  const onMessage = (e: MessageEvent<IKWorkerResult>) => {
    worker.removeEventListener("message", onMessage);
    // Discard stale results
    if (seq === _solveSeq) {
      _latestResult = e.data;
      _solveInFlight = false;
    }
  };

  worker.addEventListener("message", onMessage);
  worker.postMessage(msg);
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Initialize the worker with rest poses and constraint overrides.
 * Must be called before any solve requests (typically on mannequin mount).
 */
export async function initIKWorker(
  restPoses: Map<string, Quat>,
  constraintOverrides: Record<string, JointConstraint>,
  morphTargetDictionary?: Record<string, number>,
): Promise<void> {
  const msg: IKWorkerInit = {
    type: "init",
    restPoses: [...restPoses.entries()],
    constraintOverrides,
    morphTargetDictionary,
  };
  await postAndWait(msg);
  _initialized = true;
}

/** Whether the worker has been initialized. */
export function isIKWorkerReady(): boolean {
  return _initialized;
}

/**
 * Dispatch a chain-based IK solve (Jacobian or analytical).
 * Non-blocking — result is stashed for polling via consumeLatestResult().
 */
export function dispatchChainSolve(params: {
  solverType: "analytical" | "jacobian";
  chain: IKChainStateCore;
  target: Vec3;
  activeBone: string;
  jointWeights?: number[];
  constraintWeight: number;
  poleDirection?: Vec3;
  viewBias?: { forward: Vec3; origMidDepth: number; strength: number };
  excludeLastBone?: boolean;
  currentBonePoses: Array<[string, Quat]>;
  hasSession: boolean;
  isFirstFrame: boolean;
}): void {
  const msg: IKWorkerSolveChain = {
    type: "solveChain",
    ...params,
  };
  dispatchSolve(msg);
}

/**
 * Dispatch a whole-body IK solve.
 * Non-blocking — result is stashed for polling via consumeLatestResult().
 */
export function dispatchWholeBodySolve(params: {
  skeleton: SkeletonStateCore;
  targets: WholeBodyTargetCore[];
  constraintWeight: number;
  activeBone: string;
  lockedBones?: string[];
  currentBonePoses: Array<[string, Quat]>;
  hasSession: boolean;
  isFirstFrame: boolean;
}): void {
  const msg: IKWorkerSolveWholeBody = {
    type: "solveWholeBody",
    ...params,
  };
  dispatchSolve(msg);
}

/**
 * Consume the latest solve result. Returns null if no result is ready.
 * The result is consumed (cleared) after reading.
 */
export function consumeLatestResult(): IKWorkerResult | null {
  const result = _latestResult;
  _latestResult = null;
  return result;
}

/** Whether a solve is currently in-flight. */
export function isSolveInFlight(): boolean {
  return _solveInFlight;
}

/**
 * Request corrective weights computation only (no IK solve).
 * Used during FK rotation or when bones change without IK.
 */
export function dispatchCorrectiveWeights(
  bonePoses: Array<[string, Quat]>,
): void {
  const msg: IKWorkerSolveFK = {
    type: "solveFK",
    currentBonePoses: bonePoses,
  };
  // Use the same dispatch mechanism
  const seq = ++_solveSeq;
  _solveInFlight = true;
  const worker = getWorker();
  const onMessage = (e: MessageEvent<IKWorkerResult>) => {
    worker.removeEventListener("message", onMessage);
    if (seq === _solveSeq) {
      _latestResult = e.data;
      _solveInFlight = false;
    }
  };
  worker.addEventListener("message", onMessage);
  worker.postMessage(msg);
}

/**
 * Create a stabilizer session in the worker. Call on drag start.
 */
export function createWorkerSession(): void {
  postFire({ type: "createSession" });
}

/**
 * Destroy the stabilizer session. Call on drag end.
 */
export function destroyWorkerSession(): void {
  postFire({ type: "destroySession" });
}

/**
 * Update constraint overrides in the worker.
 */
export function updateWorkerConstraintOverrides(
  overrides: Record<string, JointConstraint>,
): void {
  postFire({ type: "updateOverrides", constraintOverrides: overrides });
}
